import { useCallback, useEffect, useRef, useState } from "react";

export interface CursorPage<T, C> {
  rows: readonly T[];
  /** Where the next (older) page starts; null when the server has no older row. */
  next: C | null;
}

export interface CursorListOptions<T> {
  /** Rows asked per page (the `limit` passed to fetchPage). A change starts again at page one. */
  pageSize: number;
  /** Re-read the first page this often while the tab is visible; omitted: never. */
  pollMs?: number | undefined;
  /** A row's identity (to tell new rows on the first page). */
  keyOf: (row: T) => string;
  /** What the list depends on (network, filters). A change starts again at page one. */
  deps: readonly unknown[];
}

export interface CursorList<T> {
  /** The page in view; null until its first answer (and after a reset). */
  rows: readonly T[] | null;
  /** 0-based index of the page in view. */
  page: number;
  /** "Showing first–last", 1-based; both 0 when no row is shown. */
  first: number;
  last: number;
  hasNewer: boolean;
  hasOlder: boolean;
  /** A page read (not a poll) is in flight; the rows in view stay meanwhile. */
  loading: boolean;
  /** The last page read's failure, or null; the rows in view stay. */
  error: unknown;
  /** On an older page: the first page now starts with a row it did not start with when last shown. */
  newAbove: boolean;
  newer(): void;
  older(): void;
  /** Back to page one (what "New … above" does). */
  toFirst(): void;
  /** Read the page in view again from its cursor. */
  retry(): void;
}

type FetchPage<T, C> = (
  cursor: C | null,
  limit: number,
  ctx: { signal: AbortSignal; silent: boolean },
) => Promise<CursorPage<T, C>>;

interface Entry<C> {
  cursor: C | null;
  offset: number;
}

interface View<T, C> {
  /** The page size and deps this view was read for. */
  key: readonly unknown[];
  stack: readonly Entry<C>[];
  rows: readonly T[] | null;
  next: C | null;
  loading: boolean;
  error: unknown;
  newAbove: boolean;
}

interface Read {
  gen: number;
  controller: AbortController;
}

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, i) => Object.is(value, b[i]));
}

function freshView<T, C>(key: readonly unknown[]): View<T, C> {
  return { key, stack: [{ cursor: null, offset: 0 }], rows: null, next: null, loading: true, error: null, newAbove: false };
}

export function useCursorList<T, C = string>(
  fetchPage: FetchPage<T, C>,
  options: CursorListOptions<T>,
): CursorList<T> {
  const { pageSize, pollMs, keyOf, deps } = options;
  const key: readonly unknown[] = [pageSize, ...deps];

  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;
  const sizeRef = useRef(pageSize);
  sizeRef.current = pageSize;

  const viewRef = useRef<View<T, C>>(freshView<T, C>(key));
  const [, setRenders] = useState(0);
  const genRef = useRef(0);
  const pageReadRef = useRef<Read | null>(null);
  const pollRef = useRef<Read | null>(null);
  /** The first row's key when page one was last shown. */
  const headKeyRef = useRef<string | null>(null);

  const commit = useCallback((patch: Partial<View<T, C>>) => {
    viewRef.current = { ...viewRef.current, ...patch };
    setRenders((n) => n + 1);
  }, []);

  const headOf = useCallback(
    (rows: readonly T[]): string | null => (rows.length === 0 ? null : keyOfRef.current(rows[0] as T)),
    [],
  );

  const call = useCallback((cursor: C | null, read: Read, silent: boolean): Promise<CursorPage<T, C>> => {
    try {
      return Promise.resolve(fetchRef.current(cursor, sizeRef.current, { signal: read.controller.signal, silent }));
    } catch (error) {
      return Promise.reject(error);
    }
  }, []);

  const stopReads = useCallback(() => {
    pageReadRef.current?.controller.abort();
    pageReadRef.current = null;
    pollRef.current?.controller.abort();
    pollRef.current = null;
  }, []);

  /** A page read supersedes every read in flight, the poll's included. */
  const readPage = useCallback(
    (cursor: C | null, apply: (page: CursorPage<T, C>) => void) => {
      stopReads();
      const read: Read = { gen: ++genRef.current, controller: new AbortController() };
      pageReadRef.current = read;
      commit({ loading: true });
      call(cursor, read, false).then(
        (page) => {
          if (read.controller.signal.aborted || pageReadRef.current?.gen !== read.gen) return;
          pageReadRef.current = null;
          apply(page);
        },
        (error: unknown) => {
          if (read.controller.signal.aborted || pageReadRef.current?.gen !== read.gen) return;
          pageReadRef.current = null;
          commit({ loading: false, error });
        },
      );
    },
    [call, commit, stopReads],
  );

  const showFirst = useCallback(
    (page: CursorPage<T, C>) => {
      headKeyRef.current = headOf(page.rows);
      commit({
        stack: [{ cursor: null, offset: 0 }],
        rows: page.rows,
        next: page.next,
        loading: false,
        error: null,
        newAbove: false,
      });
    },
    [commit, headOf],
  );

  useEffect(() => {
    headKeyRef.current = null;
    viewRef.current = freshView<T, C>(key);
    readPage(null, showFirst);
    return stopReads;
    // The list restarts on its page size and the caller's deps, spread in.
  }, key);

  const poll = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (pageReadRef.current !== null || pollRef.current !== null) return;
    const onFirst = viewRef.current.stack.length === 1;
    const read: Read = { gen: ++genRef.current, controller: new AbortController() };
    pollRef.current = read;
    call(null, read, true).then(
      (page) => {
        if (read.controller.signal.aborted || pollRef.current?.gen !== read.gen) return;
        pollRef.current = null;
        if (onFirst) {
          headKeyRef.current = headOf(page.rows);
          commit({ rows: page.rows, next: page.next, error: null });
        } else if (headOf(page.rows) !== headKeyRef.current) {
          commit({ newAbove: true });
        }
      },
      () => {
        if (pollRef.current?.gen === read.gen) pollRef.current = null;
      },
    );
  }, [call, commit, headOf]);

  useEffect(() => {
    if (pollMs === undefined || !(pollMs > 0)) return undefined;
    const timer = setInterval(poll, pollMs);
    return () => clearInterval(timer);
  }, [pollMs, poll]);

  const older = useCallback(() => {
    const view = viewRef.current;
    if (view.next === null || view.rows === null || pageReadRef.current !== null) return;
    const cursor = view.next;
    const offset = (view.stack[view.stack.length - 1]?.offset ?? 0) + view.rows.length;
    readPage(cursor, (page) => {
      if (page.rows.length === 0) {
        commit({ next: null, loading: false, error: null });
        return;
      }
      commit({
        stack: [...viewRef.current.stack, { cursor, offset }],
        rows: page.rows,
        next: page.next,
        loading: false,
        error: null,
      });
    });
  }, [commit, readPage]);

  const newer = useCallback(() => {
    const view = viewRef.current;
    if (view.stack.length < 2 || pageReadRef.current !== null) return;
    const stack = view.stack.slice(0, -1);
    const target = stack[stack.length - 1] as Entry<C>;
    readPage(target.cursor, (page) => {
      if (stack.length === 1) {
        showFirst(page);
        return;
      }
      commit({ stack, rows: page.rows, next: page.next, loading: false, error: null });
    });
  }, [commit, readPage, showFirst]);

  const toFirst = useCallback(() => readPage(null, showFirst), [readPage, showFirst]);

  const retry = useCallback(() => {
    const stack = viewRef.current.stack;
    const entry = stack[stack.length - 1] as Entry<C>;
    readPage(entry.cursor, (page) => {
      if (stack.length === 1) {
        showFirst(page);
        return;
      }
      commit({ rows: page.rows, next: page.next, loading: false, error: null });
    });
  }, [commit, readPage, showFirst]);

  const view = sameKey(viewRef.current.key, key) ? viewRef.current : freshView<T, C>(key);
  const offset = view.stack[view.stack.length - 1]?.offset ?? 0;
  const shown = view.rows === null ? 0 : view.rows.length;
  return {
    rows: view.rows,
    page: view.stack.length - 1,
    first: shown === 0 ? 0 : offset + 1,
    last: shown === 0 ? 0 : offset + shown,
    hasNewer: view.stack.length > 1,
    hasOlder: view.next !== null,
    loading: view.loading,
    error: view.error,
    newAbove: view.newAbove,
    newer,
    older,
    toFirst,
    retry,
  };
}
